import { type UseQueryResult, useQueries } from '@tanstack/react-query';

import {
  getWorkspaceAgentGuidedVariantBrandKit,
  type WorkspaceAgentGuidedVariantBrandKit,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantBrandKit';
import {
  getPrivateContextSourcesGetOptions,
  type PrivateContextSourcesGetResult,
} from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { usePrivateContextSourcesList } from '~/components/v2/hooks/queries/usePrivateContextSourcesList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

const getBrandKits = (
  results: UseQueryResult<PrivateContextSourcesGetResult>[],
): WorkspaceAgentGuidedVariantBrandKit[] => {
  const brandKits: WorkspaceAgentGuidedVariantBrandKit[] = [];

  for (const result of results) {
    if (!result.data) {
      continue;
    }

    const brandKit = getWorkspaceAgentGuidedVariantBrandKit(result.data);

    if (brandKit) {
      brandKits.push(brandKit);
    }
  }

  return brandKits;
};

export const usePrivateWorkspaceAgentGuidedVariantBrandKits = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { data: listData, isFetched: isListFetched } = usePrivateContextSourcesList({ workspaceId }, { enabled: true });
  const sources = listData?.data ?? [];
  const queries = workspaceId
    ? sources.map((source) =>
        getPrivateContextSourcesGetOptions({ contextSourceId: source.id, workspaceId }, { enabled: true }),
      )
    : [];

  const brandKitQueries = useQueries({
    combine: (results) => ({
      brandKits: getBrandKits(results),
      isBrandKitsReady: isListFetched && (sources.length === 0 || results.every((result) => result.isFetched)),
    }),
    queries,
  });

  return {
    brandKits: brandKitQueries.brandKits,
    isLoading: !!workspaceId && !brandKitQueries.isBrandKitsReady,
  };
};
